import { profileOf } from './alphabet';
import { angerSpeedDivisor, angerStage } from './anger';
import { type CellIndex, type Dir, GRID_VERSION, manhattan, stepIndex } from './grid';
import { rulesHash } from './hash';
import { type ContentPack, type Question, orderQuestions, packHash, validatePack } from './pack';
import { segmentIdeals, selectWords, sumIdeal } from './quiz';
import { assertRevivePool } from './revive';
import { cursor, hashSeed } from './rng';
import { type EngineRules, assertEngineRules } from './rules';
import { normalizeSeed } from './seedText';
import { spawnTiles } from './spawn';
import { EMPTY_REVIVE, type EngineCtx, type EngineState, type Tile, type WordState } from './types';
import { ENGINE_VERSION } from './version';

export interface SessionInfo {
  readonly sessionSeed: string;
  readonly runIndex: number;
}

export function createCtx(pack: ContentPack, rules: EngineRules): EngineCtx {
  assertEngineRules(rules);
  validatePack(pack);
  const prof = profileOf(pack);
  // 규칙(wordLenProfile)은 팩 로케일의 프로필과 같아야 한다 — en 팩을 ko 규칙으로 돌리면 길이 버킷이 비어 터진다
  if (
    rules.wordLenProfile.length !== prof.wordLenProfile.length ||
    rules.wordLenProfile.some((L, i) => L !== prof.wordLenProfile[i])
  ) {
    throw new Error(
      `RULES 위반: wordLenProfile 이 팩 로케일(${prof.locale}) 프로필과 다르다 — rulesForLocale 을 쓴다`,
    );
  }
  const ctx: EngineCtx = {
    rules,
    pack,
    alphabet: prof.alphabet,
    ordered: orderQuestions(pack),
    engineRulesHash: rulesHash(rules),
    packHash: packHash(pack),
  };
  assertRevivePool(ctx);
  return ctx;
}

export function findQuestion(ctx: EngineCtx, id: string): Question {
  for (const q of ctx.ordered) if (q.id === id) return q;
  throw new Error(`findQuestion: '${id}' 없음`);
}

function initialSnake(rules: EngineRules): CellIndex[] {
  const back = ((rules.snakeStartDir + 2) & 3) as Dir;
  const cells: CellIndex[] = [rules.snakeStartCell];
  let i = rules.snakeStartCell;
  for (let n = 1; n < rules.snakeLenInit; n++) {
    i = stepIndex(i, back);
    if (i < 0) throw new Error('initialSnake: 시작 몸통이 벽을 넘는다');
    cells.push(i);
  }
  return cells;
}

/** 논리 길이 = 물리 셀 + 미소비 pending. 모든 델타 직후 [lenMin, lenMax) 안이어야 한다. */
export function logicalLength(s: {
  readonly snake: { readonly cells: readonly number[]; readonly pending: number };
}): number {
  return s.snake.cells.length + s.snake.pending;
}

export function freshWord(index: number, q: Question, tick: number, len0: number): WordState {
  return {
    index,
    questionId: q.id,
    answer: q.a,
    eaten: 0,
    showTick: tick,
    spawnTick: tick,
    wordTick: 0,
    movedTicks: 0,
    idealTicks: 1,
    segMoved: [],
    segIdeal: [],
    segBoundary: 0,
    wrongEats: 0,
    decoyEats: 0,
    outOfOrderEats: 0,
    wrongJamoSim: [],
    hitCells: [],
    firstTurnTick: -1,
    relocations: 0,
    reviveInWord: false,
    reviveRelocations: 0,
    perfect: true,
    len0,
    autoGrown: 0,
    wrongGrown: 0,
    bestRemaining: Number.MAX_SAFE_INTEGER,
    noProgressTicks: 0,
    sealedTicks: 0,
  };
}

/** 타일 스폰 직후 우회율 분모를 봉인한다. */
export function beginWord(state: EngineState, tiles: readonly Tile[]): EngineState {
  const head = state.snake.cells[0] as CellIndex;
  const cells: CellIndex[] = [];
  for (let k = 0; ; k++) {
    const t = tiles.find((x) => x.kind === 0 && x.slot === k);
    if (t === undefined) break;
    cells.push(t.cell);
  }
  const segIdeal = segmentIdeals(head, state.snake.dir, cells);
  return {
    ...state,
    word: {
      ...state.word,
      segIdeal,
      idealTicks: sumIdeal(segIdeal),
      bestRemaining: cells.length > 0 ? manhattan(head, cells[0] as CellIndex) : 0,
    },
  };
}

export function createState(seed: string, ctx: EngineCtx, session?: SessionInfo): EngineState {
  const norm = normalizeSeed(seed);
  const sessionSeed = session === undefined ? norm : normalizeSeed(session.sessionSeed);
  const runIndex = session === undefined ? 0 : session.runIndex;
  const rules = ctx.rules;

  const words = selectWords(ctx.ordered, sessionSeed, runIndex, rules.wordLenProfile);
  const q0 = findQuestion(ctx, words[0] as string);
  const snake = initialSnake(rules);

  const layout = cursor(hashSeed(norm, 'layout'));
  const decoy = cursor(hashSeed(norm, 'decoy'));

  const spawn = spawnTiles(
    {
      head: snake[0] as CellIndex,
      dir: rules.snakeStartDir,
      body: snake,
      question: q0,
      rules,
      alphabet: ctx.alphabet,
    },
    layout,
    decoy,
  );
  let degraded = 0;
  let relaxed = 0;
  for (const e of spawn.events) {
    if (e.kind === 'spawn_degraded') degraded++;
    if (e.kind === 'spawn_relaxed') relaxed++;
  }

  const base: EngineState = {
    v: 1,
    engineVersion: ENGINE_VERSION,
    gridVersion: GRID_VERSION,
    seed: norm,
    sessionSeed,
    runIndex,
    packVersion: ctx.pack.version,
    packHash: ctx.packHash,
    engineRulesHash: ctx.engineRulesHash,
    tick: 0,
    nominalMs: 0,
    phase: 'play',
    endReason: 'running',
    readyTicksLeft: rules.readyTicks,
    clearTicksLeft: 0,
    snake: { cells: snake, dir: rules.snakeStartDir, pending: 0 },
    tiles: spawn.tiles,
    word: freshWord(0, q0, 0, snake.length),
    words,
    penalty: { status: 0, ticksLeft: 0, deferTicksLeft: 0 },
    revive: EMPTY_REVIVE,
    reviveTicks: 0,
    rng: {
      quiz: hashSeed(sessionSeed, 'quiz'),
      layout: layout.s,
      decoy: decoy.s,
      respawn: hashSeed(norm, 'respawn'),
      revive: hashSeed(norm, 'revive'), // 판 시드 — 세션 시드면 한 세션의 전 판이 같은 부활 문제를 받는다
    },
    moveTicks: 0,
    growTicks: 0,
    growPauseLeft: 0,
    peakLength: snake.length,
    lastSolveTick: -1,
    ledger: { init: rules.snakeLenInit, byTime: 0, byAnswer: 0, byWord: 0, byWrong: 0 },
    spawnDegraded: degraded,
    spawnRelaxed: relaxed,
    records: [],
    events: [{ kind: 'word_spawn', index: 0, id: q0.id, idealTicks: 0 }, ...spawn.events],
  };
  return beginWord(base, spawn.tiles);
}

/** 실효 틱 간격 — 페널티 중에는 절반(하한 60ms). 부활 페이즈는 전용 고정 틱. 명목 시간과 무관하다. */
export function tickMs(s: EngineState, ctx: EngineCtx): number {
  if (s.phase === 'revive') return ctx.rules.reviveTickMs;
  const base = ctx.rules.tickMsTable[s.word.index] as number;
  // 분노 가속(§1.16) × 페널티 배수, 하한은 페널티 바닥 하나
  const anger = angerSpeedDivisor(angerStage(s.word.wordTick, ctx.rules), ctx.rules);
  const pen = s.penalty.status === 2 ? ctx.rules.penaltySpeedDivisor : 1;
  return Math.max(ctx.rules.penaltyTickFloorMs, Math.round(base / (anger * pen)));
}

/**
 * 명목 틱 ms — 상수. 문제별 테이블을 누적하면 같은 봉인 계층에서
 * "틱이 적은데 표시 시간이 긴" 역전이 생긴다(실측 8.9%). tick × 135 는 틱의 순증가 함수다.
 */
export function nominalTickMs(_s: EngineState, ctx: EngineCtx): number {
  return ctx.rules.nominalTickMs;
}

export function isOver(s: EngineState): boolean {
  return s.phase === 'over';
}

/** 뷰에 넘길 안전한 타일 목록. kind·slot 을 제거하고 셀 인덱스 오름차순으로 정렬한다. */
export function toTileViews(s: EngineState): readonly { cell: number; ch: string }[] {
  return s.tiles.map((t) => ({ cell: t.cell, ch: t.ch })).sort((a, b) => a.cell - b.cell);
}

/** 상단 띠 슬롯. 이미 먹은 글자만 채우고 나머지는 null. */
export function toAnswerSlots(s: EngineState): readonly (string | null)[] {
  const out: (string | null)[] = [];
  for (let k = 0; k < s.word.answer.length; k++) {
    out.push(k < s.word.eaten ? s.word.answer.charAt(k) : null);
  }
  return out;
}
